import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { io } from "socket.io-client";
import axiosInstance from "../../Utils/axiosInstance";
import { getToken } from "../../Utils/auth";
import toast from "react-hot-toast";

const SERVER_ROOT_URL = process.env.REACT_APP_SERVER_ROOT_URL;

const NotificationContext = createContext(null);

let socket = null;

export const NOTIFICATION_TYPE_CONFIG = {
  // ───────────────── ORDER FLOW ─────────────────
  ORDER_CREATED: {
    emoji: "📦",
    icon: "📦",
    color: "#0C447C",
    bg: "#E6F1FB",
    border: "border-l-blue-500",
    label: "Order Created",
  },
  NEW_ORDER: {
    emoji: "🆕",
    icon: "🆕",
    color: "#0C447C",
    bg: "#E6F1FB",
    border: "border-l-blue-500",
    label: "New Order",
  },
  ORDER_REVIEW_REJECTED: {
    emoji: "❌",
    icon: "❌",
    color: "#991B1B",
    bg: "#FEE2E2",
    border: "border-l-red-500",
    label: "Order Rejected",
  },
  ORDER_REVIEW_APPROVED: {
    emoji: "✅",
    icon: "✅",
    color: "#166534",
    bg: "#DCFCE7",
    border: "border-l-green-500",
    label: "Order Approved",
  },

  // ───────────────── QUOTATION ─────────────────
  QUOTATION_RECEIVED: {
    emoji: "💰",
    icon: "💰",
    color: "#4338CA",
    bg: "#EEF2FF",
    border: "border-l-indigo-500",
    label: "Quotation",
  },
  QUOTATION_REVIEW_UPDATED: {
    emoji: "📋",
    icon: "📋",
    color: "#4338CA",
    bg: "#EEF2FF",
    border: "border-l-indigo-500",
    label: "Quotation Updated",
  },
  FIRST_QUOTATION_BONUS: {
    emoji: "🎁",
    icon: "🎁",
    color: "#7C3AED",
    bg: "#F5F3FF",
    border: "border-l-purple-500",
    label: "Bonus",
  },
  QUOTATION_SELECTION: {
    emoji: "📑",
    icon: "📑",
    color: "#6D28D9",
    bg: "#F3E8FF",
    border: "border-l-violet-500",
    label: "Quotation Selected",
  },
  QUOTATION_SELECTED: {
    emoji: "✅",
    icon: "✅",
    color: "#7C3AED",
    bg: "#F5F3FF",
    border: "border-l-purple-500",
    label: "Selected",
  },

  // ───────────────── PROFORMA & INVOICES ─────────────────
  PROFORMA_INVOICE_REQUESTED: {
    emoji: "📄",
    icon: "📄",
    color: "#92400E",
    bg: "#FEF3C7",
    border: "border-l-yellow-500",
    label: "Proforma Requested",
  },
  SUPPLIER_PROFORMA_UPLOADED: {
    emoji: "📤",
    icon: "📤",
    color: "#92400E",
    bg: "#FEF3C7",
    border: "border-l-yellow-500",
    label: "Proforma Uploaded",
  },
  SUPPLIER_SAMPLE_PROFORMA_UPLOADED: {
    emoji: "📦",
    icon: "📦",
    color: "#92400E",
    bg: "#FEF3C7",
    border: "border-l-yellow-500",
    label: "Sample Proforma",
  },
  SUPPLIER_GST_INVOICE_UPLOADED: {
    emoji: "📑",
    icon: "📑",
    color: "#166534",
    bg: "#DCFCE7",
    border: "border-l-green-500",
    label: "GST Invoice",
  },
  SUPPLIER_SAMPLE_GST_INVOICE_UPLOADED: {
    emoji: "📑",
    icon: "📑",
    color: "#166534",
    bg: "#DCFCE7",
    border: "border-l-green-500",
    label: "Sample GST",
  },
  ORIGINAL_INVOICE_UPLOADED: {
    emoji: "📄",
    icon: "📄",
    color: "#166534",
    bg: "#DCFCE7",
    border: "border-l-green-500",
    label: "Invoice",
  },

  // ───────────────── TRANSPORT & OFM ─────────────────
  TRANSPORT_CHARGE_REQUESTED: {
    emoji: "💵",
    icon: "💵",
    color: "#9A3412",
    bg: "#FFEDD5",
    border: "border-l-orange-500",
    label: "Transport Charge",
  },
  OFM_ASSIGNMENT_REQUESTED: {
    emoji: "🧑‍💼",
    icon: "🧑‍💼",
    color: "#9A3412",
    bg: "#FFEDD5",
    border: "border-l-orange-500",
    label: "OFM Assigned",
  },
  OFM_ASSIGNMENT_ACCEPTED: {
    emoji: "🤝",
    icon: "🤝",
    color: "#92400E",
    bg: "#FEF3C7",
    border: "border-l-amber-500",
    label: "OFM Accepted",
  },
  OFM_IMPRESSION_ADDED: {
    emoji: "📝",
    icon: "📝",
    color: "#155E75",
    bg: "#CFFAFE",
    border: "border-l-cyan-500",
    label: "OFM Update",
  },
  OFM_VISIT_PASS_GENERATED: {
    emoji: "🎫",
    icon: "🎫",
    color: "#0369A1",
    bg: "#E0F2FE",
    border: "border-l-sky-500",
    label: "Visit Pass Issued",
  },

  // ───────────────── DELIVERY ─────────────────
  ORDER_DISPATCHED: {
    emoji: "🚚",
    icon: "🚚",
    color: "#633806",
    bg: "#FAEEDA",
    border: "border-l-sky-500",
    label: "Dispatched",
  },
  TRACKING_DETAILS_UPDATED: {
    emoji: "📍",
    icon: "📍",
    color: "#633806",
    bg: "#FAEEDA",
    border: "border-l-sky-500",
    label: "Tracking Updated",
  },
  ORDER_DELIVERED: {
    emoji: "📬",
    icon: "📬",
    color: "#27500A",
    bg: "#EAF3DE",
    border: "border-l-green-500",
    label: "Delivered",
  },

  // ───────────────── SAMPLE & LC ─────────────────
  SAMPLE_APPROVAL_UPDATED: {
    emoji: "🧪",
    icon: "🧪",
    color: "#065F46",
    bg: "#D1FAE5",
    border: "border-l-emerald-500",
    label: "Sample",
  },
  LC_DOCUMENT_UPLOADED: {
    emoji: "📄",
    icon: "📄",
    color: "#92400E",
    bg: "#FEF3C7",
    border: "border-l-yellow-500",
    label: "LC Uploaded",
  },
  LC_DOCUMENT_REVIEWED: {
    emoji: "✔️",
    icon: "✔️",
    color: "#166534",
    bg: "#DCFCE7",
    border: "border-l-lime-500",
    label: "LC Reviewed",
  },

  // ───────────────── PAYMENT ─────────────────
  BANK_TRANSFER_PROOF_UPLOADED: {
    emoji: "🏦",
    icon: "🏦",
    color: "#0C447C",
    bg: "#E6F1FB",
    border: "border-l-blue-500",
    label: "Payment Proof",
  },
  BANK_TRANSFER_VERIFIED: {
    emoji: "✅",
    icon: "✅",
    color: "#166534",
    bg: "#DCFCE7",
    border: "border-l-green-500",
    label: "Verified",
  },
  SUPPLIER_PAYMENT_CONFIRMED: {
    emoji: "💳",
    icon: "💳",
    color: "#166534",
    bg: "#DCFCE7",
    border: "border-l-green-500",
    label: "Payment Confirmed",
  },

  // ───────────────── SATISFACTION & DISPUTES ─────────────────
  FULL_ORDER_SATISFIED: {
    emoji: "🎉",
    icon: "🎉",
    color: "#0F766E",
    bg: "#CCFBF1",
    border: "border-l-teal-500",
    label: "Satisfied",
  },
  DISPUTE_RAISED: {
    emoji: "⚠️",
    icon: "⚠️",
    color: "#991B1B",
    bg: "#FEE2E2",
    border: "border-l-red-500",
    label: "Dispute",
  },
  DISPUTE_VERIFICATION_STARTED: {
    emoji: "🔍",
    icon: "🔍",
    color: "#991B1B",
    bg: "#FEE2E2",
    border: "border-l-red-500",
    label: "Verifying",
  },
  DISPUTE_REJECTED: {
    emoji: "❌",
    icon: "❌",
    color: "#7C2D12",
    bg: "#FFEDD5",
    border: "border-l-orange-500",
    label: "Rejected",
  },

  // ───────────────── RETURNS & REFUNDS ─────────────────
  RETURN_REQUEST_APPROVED: {
    emoji: "↩️",
    icon: "↩️",
    color: "#166534",
    bg: "#DCFCE7",
    border: "border-l-green-500",
    label: "Return Approved",
  },
  RETURN_REQUEST_REJECTED: {
    emoji: "❌",
    icon: "❌",
    color: "#991B1B",
    bg: "#FEE2E2",
    border: "border-l-red-500",
    label: "Return Rejected",
  },
  RETURN_DISPATCHED: {
    emoji: "🚚",
    icon: "🚚",
    color: "#633806",
    bg: "#FAEEDA",
    border: "border-l-sky-500",
    label: "Return Shipped",
  },
  RETURN_TRACKING_UPDATED: {
    emoji: "📍",
    icon: "📍",
    color: "#633806",
    bg: "#FAEEDA",
    border: "border-l-sky-500",
    label: "Return Tracking",
  },
  RETURN_DELIVERED: {
    emoji: "📬",
    icon: "📬",
    color: "#27500A",
    bg: "#EAF3DE",
    border: "border-l-green-500",
    label: "Return Received",
  },
  REFUND_PROCESSED: {
    emoji: "💰",
    icon: "💰",
    color: "#166534",
    bg: "#DCFCE7",
    border: "border-l-green-500",
    label: "Refund Processed",
  },
  REFUND_RECEIVED_CONFIRMATION: {
    emoji: "✅",
    icon: "✅",
    color: "#166534",
    bg: "#DCFCE7",
    border: "border-l-green-500",
    label: "Refund Confirmed",
  },

  // ───────────────── CRM ─────────────────
  CRM_ASSIGNED: {
    emoji: "👤",
    icon: "👤",
    color: "#0C447C",
    bg: "#E6F1FB",
    border: "border-l-blue-500",
    label: "CRM Assigned",
  },
};

// Default fallback
export const DEFAULT_NOTIFICATION_STYLE = {
  emoji: "🔔",
  icon: "🔔",
  color: "#666",
  bg: "#eee",
  border: "border-l-gray-400",
  label: "Update",
};

const vibrationPatterns = {
  NEW_ORDER: [300, 150, 300],
  ORDER_REJECTED: [200, 100, 200],
  ORDER_CREATED: [300, 150, 300],
  QUOTATION_RECEIVED: [400, 100, 400],
  QUOTATION_SELECTION: [400, 100, 400],
  NEW_QUOTATION: [300, 100, 300, 100, 300],
  QUOTATION_SELECTED: [500],
  ORDER_DISPATCHED: [200, 100, 200],
  ORDER_DELIVERED: [500, 150, 500],
};

const notificationAudio = new Audio("/appnotification.mp3");

notificationAudio.preload = "auto";

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  // ───────────────── FETCH NOTIFICATIONS ─────────────────
  const fetchNotifications = useCallback(
    async (pageNumber = 1, append = false) => {
      try {
        setLoading(true);

        const { data } = await axiosInstance.get(
          `/notification?page=${pageNumber}&limit=15`,
        );

        if (data.success) {
          const newNotifications = data.data.notifications;

          setNotifications((prev) =>
            append ? [...prev, ...newNotifications] : newNotifications,
          );

          setUnreadCount(data.data.unreadCount);
          setHasMore(data.data.pagination.hasMore);
          setPage(pageNumber);
        }
      } catch (err) {
        console.error(
          "[Notifications] Fetch failed:",
          err.response?.data || err.message,
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const loadMoreNotifications = useCallback(() => {
    if (!hasMore || loading) return;

    fetchNotifications(page + 1, true);
  }, [page, hasMore, loading, fetchNotifications]);

  // ───────────────── MARK AS READ ─────────────────
  const markAsRead = useCallback(async (id) => {
    try {
      await axiosInstance.patch(`/notification/${id}/read`);

      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)),
      );

      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("[Buyer Notifications] markAsRead failed:", err.message);
    }
  }, []);

  // ───────────────── MARK ALL AS READ ─────────────────
  const markAllAsRead = useCallback(async () => {
    try {
      await axiosInstance.patch(`/notification/read-all`);

      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));

      setUnreadCount(0);
    } catch (err) {
      console.error("[Buyer Notifications] markAllAsRead failed:", err.message);
    }
  }, []);

  // ───────────────── DELETE NOTIFICATION ─────────────────
  const deleteNotification = useCallback(async (id) => {
    try {
      await axiosInstance.delete(`/notification/${id}`);

      setNotifications((prev) => prev.filter((n) => n._id !== id));

      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("[Buyer Notifications] delete failed:", err.message);
    }
  }, []);

  // ───────────────── SOCKET CONNECTION ─────────────────
  useEffect(() => {
    const initSocket = (token) => {
      // Tear down previous connection and state
      if (socket) {
        socket.disconnect();
        socket = null;
      }
      setNotifications([]);
      setUnreadCount(0);
      setIsConnected(false);

      if (!token) return;

      socket = io(SERVER_ROOT_URL, {
        auth: { token },
        transports: ["websocket"],
        reconnection: true,
        reconnectionDelay: 2000,
      });

      socket.on("connect", () => {
        setIsConnected(true);
        console.log("[Buyer Socket] Connected:", socket.id);
      });

      socket.on("notification:new", (notification) => {
        setNotifications((prev) => [notification, ...prev]);
        setUnreadCount((prev) => prev + 1);

        notificationAudio.currentTime = 0;
        notificationAudio.play().catch((err) => {
          console.log("[Notification Sound Blocked]:", err.message);
        });

        if ("vibrate" in navigator) {
          navigator.vibrate(vibrationPatterns[notification.type] || 300);
        }

        const style =
          NOTIFICATION_TYPE_CONFIG[notification.type] ||
          DEFAULT_NOTIFICATION_STYLE;

        toast.custom(
          (t) => (
            <div
              className={`
              ${t.visible ? "animate-[toast-enter_0.35s_ease]" : "animate-[toast-leave_0.25s_ease_forwards]"}
              max-w-sm md:max-w-md w-full bg-white shadow-2xl rounded-2xl
              pointer-events-auto overflow-hidden
              border border-gray-100 border-l-4
              ${style.border}
            `}
            >
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{style.icon}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">
                      {notification.title}
                    </p>
                    <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                      {notification.message}
                    </p>
                  </div>
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="text-gray-400 hover:text-gray-700 transition"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          ),
          { duration: 5000 },
        );
      });

      socket.on("disconnect", () => {
        setIsConnected(false);
      });

      fetchNotifications();
    };

    // Run immediately with current token (handles page refresh case)
    const currentToken = getToken();
    initSocket(currentToken);

    // Re-run on login/logout
    const handleAuthChanged = (e) => {
      initSocket(e.detail.token);
    };
    window.addEventListener("auth:changed", handleAuthChanged);

    return () => {
      window.removeEventListener("auth:changed", handleAuthChanged);
      socket?.disconnect();
      socket = null;
    };
  }, [fetchNotifications]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isConnected,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        fetchNotifications,
        loadMoreNotifications,
        hasMore,
        loading,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotifications must be used inside NotificationProvider",
    );
  }

  return context;
};

export const getNotificationSocket = () => socket;
